import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiLink, FiSave, FiDroplet, FiGrid, FiChevronDown, FiChevronRight, FiRotateCcw } from "react-icons/fi";
import { toast } from "react-toastify";
import CompactColorPicker from "./CompactColorPicker";
import AxiosUrl from "../AxiosUrl";
import { THEME_COLOR_KEYS, COLOR_GROUPS } from "./colorKeys";
import styles from "./TemplateEditorPage.module.css";

// Heuristic: given a clicked element's theme key, which other keys are
// related and should be surfaced together? (e.g. clicking the header
// surfaces HeaderColor + HeaderTextColor + HeaderHoverColor + HeaderHoverTextColor)
const RELATED_KEYS = {
    HeaderColor: ["HeaderTextColor", "HeaderHoverColor", "HeaderHoverTextColor"],
    HeaderTextColor: ["HeaderColor", "HeaderHoverColor", "HeaderHoverTextColor"],
    FooterColor: ["FooterTextColor"],
    FooterTextColor: ["FooterColor"],
    BuyNowColor: ["BuyNowTextColor"],
    BuyNowTextColor: ["BuyNowColor"],
    AddToCartColor: ["AddToCartTextColor"],
    AddToCartTextColor: ["AddToCartColor"],
    BadgeBgColor: ["BadgeTextColor"],
    BadgeTextColor: ["BadgeBgColor"],
    InputBgColor: ["InputBorderColor", "InputFocusBorderColor", "PlaceholderColor"],
    InputBorderColor: ["InputBgColor", "InputFocusBorderColor"],
    PriceColor: ["OldPriceColor"],
    BodyBgColor: ["TextPrimaryColor", "TextSecondaryColor"],
};

function TemplateEditorPage() {
    const { templateName } = useParams();
    const navigate = useNavigate();
    const iframeRef = useRef(null);
    const uid = localStorage.getItem("id");
    const storeName = localStorage.getItem("store") || "Store";
    const storeDomain = localStorage.getItem("domain") || "";

    const [template, setTemplate] = useState(null);
    const [storeTemplate, setStoreTemplate] = useState(null);
    const [colors, setColors] = useState({});
    const [originalColors, setOriginalColors] = useState({});
    const [selectedKey, setSelectedKey] = useState(null);
    const [tab, setTab] = useState("style"); // "style" | "sections"
    const [saving, setSaving] = useState(false);
    const [iframeReady, setIframeReady] = useState(false);
    const [loading, setLoading] = useState(true);

    // Load template + store-template
    useEffect(() => {
        (async () => {
            try {
                const [tplRes, storeRes] = await Promise.all([
                    AxiosUrl.get("/templates"),
                    AxiosUrl.get(`/store-template/${uid}`),
                ]);
                const tpl = (Array.isArray(tplRes.data) ? tplRes.data : []).find(
                    (t) => t.name === templateName
                );
                setTemplate(tpl || null);
                setStoreTemplate(storeRes.data || null);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load template");
            } finally {
                setLoading(false);
            }
        })();
    }, [templateName, uid]);

    // Build merged color set once template is loaded
    useEffect(() => {
        if (!template) return;
        const preset =
            template.colors?.find((c) => c.name === storeTemplate?.activeColorPreset) ||
            template.colors?.find((c) => c.isDefault) ||
            template.colors?.[0];
        const base = preset?.values || {};
        const custom = storeTemplate?.customColors || {};
        const merged = { ...base };
        for (const [k, v] of Object.entries(custom)) {
            if (v != null && v !== "") merged[k] = v;
        }
        setColors(merged);
        setOriginalColors(merged);
    }, [template, storeTemplate]);

    // postMessage handler
    useEffect(() => {
        function onMessage(event) {
            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (data.type === "editor-ready") {
                setIframeReady(true);
                if (iframeRef.current?.contentWindow) {
                    iframeRef.current.contentWindow.postMessage(
                        { type: "updateColors", colors },
                        "*"
                    );
                }
            } else if (data.type === "elementClicked") {
                setSelectedKey(data.key);
            }
        }
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [colors]);

    const pushColorToIframe = useCallback((key, value) => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { type: "updateColor", key, value },
                "*"
            );
        }
    }, []);

    function handleColorChange(key, value) {
        setColors((prev) => ({ ...prev, [key]: value }));
        pushColorToIframe(key, value);
    }

    function handleResetKey(key) {
        const original = originalColors[key];
        if (original != null) {
            setColors((prev) => ({ ...prev, [key]: original }));
            pushColorToIframe(key, original);
        }
    }

    async function handleSave() {
        if (!uid) return;
        setSaving(true);
        try {
            const preset =
                template.colors?.find((c) => c.name === storeTemplate?.activeColorPreset) ||
                template.colors?.find((c) => c.isDefault) ||
                template.colors?.[0];
            const presetVals = preset?.values || {};
            const customColors = {};
            for (const [k, v] of Object.entries(colors)) {
                if (v !== presetVals[k]) customColors[k] = v;
            }
            await AxiosUrl.post(`/set-store-template-colors/${uid}`, { colors: customColors });
            toast.success("Saved!");
            setOriginalColors(colors);
            if (iframeRef.current) {
                // eslint-disable-next-line no-self-assign
                iframeRef.current.src = iframeRef.current.src;
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    }

    const dirty = useMemo(() => {
        for (const key of Object.keys(colors)) {
            if (colors[key] !== originalColors[key]) return true;
        }
        for (const key of Object.keys(originalColors)) {
            if (!(key in colors)) return true;
        }
        return false;
    }, [colors, originalColors]);

    const previewUrl = useMemo(() => {
        if (!template) return "";
        const preset = storeTemplate?.activeColorPreset;
        return `/template-preview/${template.name}?editor=1${uid ? `&uid=${uid}` : ""
            }${preset ? `&preset=${encodeURIComponent(preset)}` : ""}`;
    }, [template, storeTemplate, uid]);

    if (loading) {
        return <div className={styles.loading}>Loading editor…</div>;
    }
    if (!template) {
        return (
            <div className={styles.loading}>
                Template "{templateName}" not found.{" "}
                <button onClick={() => navigate("/template")}>Back</button>
            </div>
        );
    }

    const selectedMeta = THEME_COLOR_KEYS.find((k) => k.key === selectedKey);
    const relatedKeys = selectedKey ? RELATED_KEYS[selectedKey] || [] : [];
    const relatedMetas = relatedKeys.map((k) => THEME_COLOR_KEYS.find((m) => m.key === k)).filter(Boolean);

    return (
        <div className={styles.page}>
            {/* Top bar */}
            <header className={styles.topbar}>
                <div className={styles.topbarLeft}>
                    <button className={styles.backBtn} onClick={() => navigate("/template")}>
                        <FiArrowLeft />
                    </button>
                    <div className={styles.topbarTitle}>
                        <div className={styles.storeName}>{storeName}</div>
                        <div className={styles.hint}>Click any element on the preview to edit it</div>
                    </div>
                </div>
                <div className={styles.topbarRight}>
                    {storeDomain && (
                        <a
                            href={`https://${storeDomain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.liveSiteBtn}
                        >
                            <FiLink /> Live Site
                        </a>
                    )}
                    <button
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!dirty || saving}
                    >
                        {saving ? "Saving…" : dirty ? <><FiSave /> Save</> : "Saved"}
                    </button>
                </div>
            </header>

            {/* Body: left preview, right sidebar */}
            <div className={styles.body}>
                <div className={styles.previewPane}>
                    <iframe
                        ref={iframeRef}
                        src={previewUrl}
                        title="Storefront Preview"
                        className={styles.iframe}
                    />
                    {!iframeReady && <div className={styles.iframeLoading}>Loading preview…</div>}
                </div>

                <aside className={styles.sidebar}>
                    <div className={styles.sidebarTabs}>
                        <button
                            className={`${styles.sidebarTab} ${tab === "style" ? styles.sidebarTabActive : ""}`}
                            onClick={() => setTab("style")}
                        >
                            <FiDroplet /> Style
                        </button>
                        <button
                            className={`${styles.sidebarTab} ${styles.sidebarTabDisabled}`}
                            title="Section editing coming in Phase 3"
                        >
                            <FiGrid /> Sections
                        </button>
                    </div>

                    {tab === "style" && (
                        <div className={styles.sidebarBody}>
                            {/* Preset switcher — switch the whole palette at once */}
                            {template.colors?.length > 1 && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}><FiChevronDown /> PRESETS ({template.colors.length})</div>
                                    <div className={styles.presetStack}>
                                        {template.colors.map((preset) => {
                                            const isCurrent = storeTemplate?.activeColorPreset === preset.name
                                                || (!storeTemplate?.activeColorPreset && preset.isDefault);
                                            const isSwitching = saving;
                                            return (
                                                <button
                                                    key={preset.name}
                                                    className={`${styles.presetRow} ${isCurrent ? styles.presetRowActive : ""}`}
                                                    disabled={isSwitching}
                                                    onClick={async () => {
                                                        // Local immediate feedback: push values to iframe
                                                        setColors(preset.values);
                                                        setOriginalColors(preset.values);
                                                        setSelectedKey(null);
                                                        if (iframeRef.current?.contentWindow) {
                                                            iframeRef.current.contentWindow.postMessage(
                                                                { type: "updateColors", colors: preset.values },
                                                                "*"
                                                            );
                                                        }
                                                        // Persist the preset switch (clears customColors so preset takes effect)
                                                        setSaving(true);
                                                        try {
                                                            await AxiosUrl.post(`/set-store-template/${uid}`, {
                                                                template: template.name,
                                                                colorPreset: preset.name,
                                                            });
                                                            setStoreTemplate((prev) => ({
                                                                ...(prev || {}),
                                                                activeTemplate: template.name,
                                                                activeColorPreset: preset.name,
                                                                customColors: {},
                                                            }));
                                                            toast.success(`Activated: ${preset.name}`);
                                                        } catch (err) {
                                                            console.error(err);
                                                            toast.error("Failed to switch preset");
                                                        } finally {
                                                            setSaving(false);
                                                        }
                                                    }}
                                                >
                                                    <span
                                                        className={styles.presetDot}
                                                        style={{ background: preset.values?.HeaderColor }}
                                                    />
                                                    <span
                                                        className={styles.presetDot}
                                                        style={{ background: preset.values?.BuyNowColor }}
                                                    />
                                                    <span
                                                        className={styles.presetDot}
                                                        style={{ background: preset.values?.AccentColor }}
                                                    />
                                                    <span className={styles.presetName}>{preset.name}</span>
                                                    {isCurrent && <span className={styles.presetCurrent}>current</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className={styles.paletteHint}>Click any preset to activate it</div>
                                </div>
                            )}

                            {/* Selected element block */}
                            <div className={styles.section}>
                                <div className={styles.sectionHeader}><FiChevronDown /> SELECTED</div>
                                {selectedMeta ? (
                                    <div className={styles.selectedCard}>
                                        <div className={styles.selectedKey}>{selectedMeta.label}</div>
                                        <code className={styles.selectedCode}>{selectedMeta.cssVar}</code>
                                        <button
                                            className={styles.deselectBtn}
                                            onClick={() => setSelectedKey(null)}
                                        >
                                            Deselect
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.selectedEmpty}>
                                        Click an element in the preview to select it, or scroll the list below.
                                    </div>
                                )}
                            </div>

                            {/* Colors — focused on selected + related */}
                            {selectedMeta && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}><FiChevronDown /> COLORS</div>
                                    <div className={styles.rowGrid}>
                                        <ColorRow
                                            meta={selectedMeta}
                                            value={colors[selectedMeta.key] || selectedMeta.defaultValue}
                                            changed={colors[selectedMeta.key] !== originalColors[selectedMeta.key]}
                                            onChange={(v) => handleColorChange(selectedMeta.key, v)}
                                            onReset={() => handleResetKey(selectedMeta.key)}
                                        />
                                        {relatedMetas.map((meta) => (
                                            <ColorRow
                                                key={meta.key}
                                                meta={meta}
                                                value={colors[meta.key] || meta.defaultValue}
                                                changed={colors[meta.key] !== originalColors[meta.key]}
                                                onChange={(v) => handleColorChange(meta.key, v)}
                                                onReset={() => handleResetKey(meta.key)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Full catalog — grouped, collapsible */}
                            <div className={styles.section}>
                                <div className={styles.sectionHeader}><FiChevronDown /> ALL COLORS</div>
                                {COLOR_GROUPS.map((group) => (
                                    <GroupBlock
                                        key={group}
                                        group={group}
                                        colors={colors}
                                        originalColors={originalColors}
                                        onChange={handleColorChange}
                                        onReset={handleResetKey}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}

function ColorRow({ meta, value, changed, onChange, onReset }) {
    return (
        <div className={`${styles.row} ${changed ? styles.rowChanged : ""}`}>
            <div className={styles.rowLabel}>{meta.label}</div>
            <div className={styles.rowControls}>
                <CompactColorPicker color={value} onChange={onChange} />
                {changed && (
                    <button className={styles.resetRow} onClick={onReset} title="Reset">
                        <FiRotateCcw />
                    </button>
                )}
            </div>
        </div>
    );
}

function GroupBlock({ group, colors, originalColors, onChange, onReset }) {
    const [open, setOpen] = useState(true);
    const keys = THEME_COLOR_KEYS.filter((k) => k.group === group);
    return (
        <div className={styles.group}>
            <button className={styles.groupHeader} onClick={() => setOpen(!open)}>
                <span>{group}</span>
                <span className={styles.groupCount}>{keys.length}</span>
                <span className={styles.groupChevron}>{open ? <FiChevronDown /> : <FiChevronRight />}</span>
            </button>
            {open && (
                <div className={styles.rowGrid}>
                    {keys.map((meta) => (
                        <ColorRow
                            key={meta.key}
                            meta={meta}
                            value={colors[meta.key] || meta.defaultValue}
                            changed={colors[meta.key] !== originalColors[meta.key]}
                            onChange={(v) => onChange(meta.key, v)}
                            onReset={() => onReset(meta.key)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default TemplateEditorPage;
